import { useEffect, useRef } from 'react';
import { SWIPE_CONFIG } from '../imageViewerUtils';

const ANIMATE_BACK_MS = 200;
const SLIDE_OUT_MS = 150;

/**
 * Hook for horizontal swipe navigation on touch devices
 * Enables swipe left/right to navigate between slides
 * Supports visual follow-the-finger effect via transformRef
 * 
 * @param {Object} options
 * @param {boolean} options.enabled - Whether swipe navigation is enabled
 * @param {Function} options.onPrev - Callback for swipe right (previous)
 * @param {Function} options.onNext - Callback for swipe left (next)
 * @param {React.RefObject} options.targetRef - Ref to the touch target element
 * @param {React.RefObject} options.transformRef - Ref to element to apply visual transform (optional)
 * @param {number} options.minSwipeDistance - Minimum swipe distance to trigger navigation (default: 50)
 * @param {boolean} options.slideOut - Whether to animate slide out before navigation (default: false)
 * @param {boolean} options.canGoPrev - Whether there's a previous slide to navigate to (default: true)
 * @param {boolean} options.canGoNext - Whether there's a next slide to navigate to (default: true)
 * @param {React.RefObject} options.closeTransformRef - Ref to element for close animation (defaults to transformRef)
 */
export const useSwipeNavigation = ({ 
    enabled, 
    onPrev, 
    onNext, 
    targetRef,
    transformRef,
    minSwipeDistance = 50,
    slideOut = false,
    canGoPrev = true,
    canGoNext = true,
    closeTransformRef,
}) => {
    // Use closeTransformRef for close animation, fallback to transformRef
    const effectiveCloseRef = closeTransformRef || transformRef;
    const touchStartRef = useRef(null);
    const horizontalLockRef = useRef(false);
    const animationTimeoutRef = useRef(null);
    const isAnimatingRef = useRef(false);
    
    useEffect(() => {
        if (!enabled) return;
        const el = targetRef?.current;
        if (!el) return;
        
        const onTouchStart = (e) => {
            if (e.touches.length === 0) return;
            if (isAnimatingRef.current) return; // Don't start new touch during animation
            
            // Clear any pending animation
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }
            // Reset transform transition
            if (transformRef?.current) {
                transformRef.current.style.transition = '';
                transformRef.current.style.transform = '';
            }
            // Reset close transform ref too
            if (effectiveCloseRef?.current && effectiveCloseRef !== transformRef) {
                effectiveCloseRef.current.style.transition = '';
                effectiveCloseRef.current.style.transform = '';
                effectiveCloseRef.current.style.opacity = '';
            }
            touchStartRef.current = { 
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
            };
            horizontalLockRef.current = false;
        };

        const onTouchMove = (e) => {
            if (!touchStartRef.current || e.touches.length === 0) return;
            if (isAnimatingRef.current) return;
            
            const deltaX = e.touches[0].clientX - touchStartRef.current.x;
            const deltaY = e.touches[0].clientY - touchStartRef.current.y;
            
            // Lock to horizontal if horizontal movement is dominant
            if (!horizontalLockRef.current) {
                if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
                    horizontalLockRef.current = true;
                }
            }
            
            // Apply visual transform and prevent scroll when swiping horizontally
            if (horizontalLockRef.current) {
                e.preventDefault();
                // Apply follow-the-finger transform, but limit at boundaries
                if (transformRef?.current) {
                    let effectiveDeltaX = deltaX;
                    
                    // At first slide, limit swiping right (positive deltaX = prev direction)
                    if (!canGoPrev && deltaX > 0) {
                        // Apply rubber band effect - diminishing returns past boundary
                        effectiveDeltaX = Math.sqrt(deltaX) * 3;
                        transformRef.current.style.transform = `translateX(${effectiveDeltaX}px)`;
                        transformRef.current.style.opacity = '';
                    }
                    // At last slide, swiping left (negative deltaX = next direction)
                    // Apply swipe-to-close style effect (scale + fade) to entire page
                    else if (!canGoNext && deltaX < 0) {
                        const absDelta = Math.abs(deltaX);
                        const scale = 1 - Math.min(1, absDelta / SWIPE_CONFIG.SCALE_DISTANCE) * SWIPE_CONFIG.SCALE_MAX;
                        const opacity = Math.max(0.2, 1 - 0.8 * Math.min(1, absDelta / SWIPE_CONFIG.SCALE_DISTANCE));
                        // Apply to entire page wrapper for close animation
                        if (effectiveCloseRef?.current) {
                            effectiveCloseRef.current.style.transform = `translateX(${deltaX}px) scale(${scale})`;
                            effectiveCloseRef.current.style.opacity = String(opacity);
                        }
                        return; // Don't also transform the image content
                    }
                    // Normal navigation - just translate
                    else {
                        transformRef.current.style.transform = `translateX(${effectiveDeltaX}px)`;
                        transformRef.current.style.opacity = '';
                    }
                }
            }
        };

        const onTouchEnd = (e) => {
            if (!touchStartRef.current || !e.changedTouches || e.changedTouches.length === 0) {
                touchStartRef.current = null;
                horizontalLockRef.current = false;
                return;
            }
            if (isAnimatingRef.current) return;
            
            const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
            const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
            
            // Only trigger if horizontal movement was dominant
            if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= minSwipeDistance) {
                const direction = deltaX > 0 ? 'prev' : 'next';
                const callback = direction === 'prev' ? onPrev : onNext;
                
                // Check if there's a slide to navigate to in this direction
                // (controls whether to show slide-out animation)
                const hasSlideInDirection = direction === 'prev' ? canGoPrev : canGoNext;
                
                if (!hasSlideInDirection && !callback) {
                    // At boundary with no callback - snap back with animation
                    if (transformRef?.current) {
                        transformRef.current.style.transition = `transform ${ANIMATE_BACK_MS}ms ease-out, opacity ${ANIMATE_BACK_MS}ms ease-out`;
                        transformRef.current.style.transform = 'translateX(0) scale(1)';
                        transformRef.current.style.opacity = '1';
                        animationTimeoutRef.current = setTimeout(() => {
                            if (transformRef?.current) {
                                transformRef.current.style.transition = '';
                            }
                            animationTimeoutRef.current = null;
                        }, ANIMATE_BACK_MS);
                    }
                } else if (hasSlideInDirection && slideOut && transformRef?.current) {
                    // Has adjacent slide - animate slide out, then navigate
                    isAnimatingRef.current = true;
                    // Use percentage to align with adjacent slides positioned at -100%/+100%
                    const targetPercent = direction === 'prev' ? 100 : -100;
                    
                    transformRef.current.style.transition = `transform ${SLIDE_OUT_MS}ms ease-out`;
                    transformRef.current.style.transform = `translateX(${targetPercent}%)`;
                    
                    animationTimeoutRef.current = setTimeout(() => {
                        // Don't reset transform - let React handle it on re-render
                        // This prevents flicker between animation end and new content
                        isAnimatingRef.current = false;
                        callback?.();
                        animationTimeoutRef.current = null;
                    }, SLIDE_OUT_MS);
                } else {
                    // No adjacent slide but has callback (e.g., close on last slide)
                    // Or slideOut disabled
                    const isCloseSwipe = !hasSlideInDirection && direction === 'next';
                    
                    if (isCloseSwipe && Math.abs(deltaX) >= SWIPE_CONFIG.MIN_SWIPE_CLOSE) {
                        // Threshold reached - close
                        callback?.();
                    } else if (isCloseSwipe) {
                        // Below threshold - animate back with scale/opacity on entire page
                        if (effectiveCloseRef?.current) {
                            effectiveCloseRef.current.style.transition = `transform ${SWIPE_CONFIG.ANIMATE_MS}ms ease-out, opacity ${SWIPE_CONFIG.ANIMATE_MS}ms ease-out`;
                            effectiveCloseRef.current.style.transform = 'translateX(0) scale(1)';
                            effectiveCloseRef.current.style.opacity = '1';
                            animationTimeoutRef.current = setTimeout(() => {
                                if (effectiveCloseRef?.current) {
                                    effectiveCloseRef.current.style.transition = '';
                                }
                                animationTimeoutRef.current = null;
                            }, SWIPE_CONFIG.ANIMATE_MS);
                        }
                    } else {
                        // slideOut disabled - navigate immediately
                        if (transformRef?.current) {
                            transformRef.current.style.transition = `transform ${ANIMATE_BACK_MS}ms ease-out`;
                            transformRef.current.style.transform = 'translateX(0)';
                        }
                        callback?.();
                    }
                }
            } else {
                // Snap back with animation
                if (transformRef?.current) {
                    transformRef.current.style.transition = `transform ${ANIMATE_BACK_MS}ms ease-out`;
                    transformRef.current.style.transform = 'translateX(0)';
                    animationTimeoutRef.current = setTimeout(() => {
                        if (transformRef?.current) {
                            transformRef.current.style.transition = '';
                        }
                        animationTimeoutRef.current = null;
                    }, ANIMATE_BACK_MS);
                }
            }
            
            touchStartRef.current = null;
            horizontalLockRef.current = false;
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [enabled, onPrev, onNext, targetRef, transformRef, minSwipeDistance, slideOut, canGoPrev, canGoNext, effectiveCloseRef]);
};

export default useSwipeNavigation;
